import { PaperworkList } from '@/components/PaperworkList';
import type { DocumentDto } from '@/api/types';

/**
 * ============================================================================
 * ⭐⭐ THE PAPERWORK TRAY'S SECOND MODE — ONE LOAD'S DOCUMENTS
 * ============================================================================
 *
 * > Pete, 2026-09-10: *"is there any clever way to have the Scanned Paperwork
 * > panel also double as the Paperwork on an appointment or driver?"* … *"lets
 * > make that fire on mouse hover instead of click. When you click on the
 * > appointment, it opens the popup so annoying."*
 *
 * ---- ⛔ IT IS A MODE OF THE TRAY, NOT A NEW PANEL -------------------------
 *
 * The rail is height-constrained — Brighton has 23 doors and four panels
 * competing for the same column — so a fifth panel would cost more than it
 * gives. ⭐ This borrows the tray's space while the cursor is dwelling on a
 * load, and gives it straight back.
 *
 * ---- ⛔⛔ THE WAITING COUNT NEVER LEAVES THE SCREEN ------------------------
 *
 * ⚠️ THE TRAY IS A WORK QUEUE. That "2 waiting" badge is how anybody knows
 * there is unfiled paperwork at all, and **a queue nobody can see is one that
 * grows.** ⛔ Without it, this trade makes the rail worse rather than better,
 * and the cost lands weeks later as a pile of unfiled scans nobody noticed.
 *
 * ⭐ IT IS NOW ON THE PANEL HEADER'S BACK BUTTON, not here. Pete removed the
 * badge from the header on 2026-09-10 — *"don't need the 2 waiting, remove
 * it"* — and the obligation moved with it rather than being dropped: the one
 * moment the number must be unmissable is the moment somebody is deciding
 * whether to go back to the queue.
 *
 * ---- ⛔ THIS COMPONENT NO LONGER DRAWS ITS OWN HEADER (2026-09-10) --------
 *
 * ⚠️ IT USED TO, and once the PANEL header started saying "Attached Paperwork ·
 * A00000311" and carrying the toggle back, this one was a second title and a
 * second way back stacked directly beneath the first. ⭐ One name, one control,
 * one place to look — the panel owns the chrome and this owns the list.
 *
 * ---- ⭐ IT REUSES `PaperworkList` RATHER THAN RE-RENDERING A LOAD ---------
 *
 * That component already lists a load's documents with per-row preview, sign
 * and print, AND carries the drop zone that uploads straight onto the load —
 * which is exactly Pete's *"it should have an attach paperwork icon on there so
 * you can upload docs there to the appointment or driver."*
 *
 * ⛔ A SECOND IMPLEMENTATION WOULD HAVE DRIFTED FROM IT WITHIN A WEEK. The two
 * would then disagree about what "signed" looks like on a row, in two places a
 * scheduler reaches by different routes.
 *
 * ---- ⚠️ AND IT IS SHARED BY BOTH TRAYS, ON PURPOSE ------------------------
 *
 * ⛔ `PaperworkSliver` (SharePoint sites) and `ScanFolderPanel` (local-folder
 * sites) each render this. On 2026-09-09 a feature built into only one of them
 * shipped green and was invisible at Pete's site — see §4.14.1. One component
 * used twice is how that stops being possible.
 * ============================================================================
 */

export interface AttachedTarget {
    appointmentId: string;
    appointmentNumber: string;
    /**
     * ⭐ WHAT THE PANEL CALLS THIS, which is not always the load's number.
     *
     * > Pete, 2026-09-10: *"The appointment number can move to line below
     * > Paperwork - Appt Number. or Driver Name, depending on what is hovered
     * > over."*
     *
     * ⛔ THE DOCUMENTS ARE STILL THE LOAD'S — `PaperworkList` takes an
     * appointment and there is one dossier per load, so hovering the driver and
     * hovering his chip show the SAME list. ⚠️ Only the name differs, and it
     * differs because it answers "what am I pointing at", not "whose file is
     * this". Conflating those would be a way to think a driver had his own pile.
     */
    label: string;
}

export function AttachedPaperworkPanel({
    target, facilityCode, getToken, onChanged, onSign, onPrint, onCount,
}: {
    target: AttachedTarget;
    facilityCode: string;
    getToken: () => Promise<string>;
    onChanged?: () => void;
    onSign?: (d: DocumentDto) => void;
    onPrint?: (d: DocumentDto) => void;
    /**
     * ⭐ PASSED STRAIGHT THROUGH TO `PaperworkList`, which is the only thing here
     * that knows how many documents there are. ⚠️ This component deliberately adds
     * nothing — it is the seam between the panel chrome and the list, and a count
     * derived HERE would be a second answer to a question already answered below.
     */
    onCount?: (n: number | null) => void;
}) {
    return (
        <div className="space-y-1.5">
            {/* ⭐ The drop zone inside this is the "attach paperwork" control — it
                uploads STRAIGHT ONTO this appointment, which is why no separate icon is
                needed beside the header. */}
            <PaperworkList
                appointmentId={target.appointmentId}
                label={target.label}
                getToken={getToken}
                facilityCode={facilityCode}
                onChanged={onChanged}
                onSign={onSign}
                onPrint={onPrint}
                onCount={onCount}
            />
        </div>
    );
}
